import { useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { Button } from 'antd';
import { RecycleBin } from '../../../../components/assets';

import { Footer, TitleBar } from '../../../../components';
import QueryErrorBoundary from '../../../../components/error-handling';
import TableV3 from '../../../../components/generic/Table/TableV3';
import QualitativeFactorDeleteModal from '../../../../components/generic/QualitativeFactorDeleteModal';
import {
  CREATE_QUALITATIVE_FACTOR,
  DELETE_QUALITATIVE_FACTOR,
  GET_QUALITATIVE_FACTORS,
  UPDATE_QUALITATIVE_FACTOR,
} from './queries';
import { QualitativeFactor } from './types';
import { qualitativeFactorsColumns } from './columns';
import { formFields } from './formFields';
import { templateColumnConfigs, templateUploadConfig } from './template';
import { baseColumns } from './export';
import TitleUpdater from '../../../../components/common/TitleUpdater';

export type BusinessFunctionsData = {
  getQualitativeFactors: QualitativeFactor[];
};

const QualitativeFactors = () => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<QualitativeFactor | null>(
    null,
  );
  const [deleteLoading, setDeleteLoading] = useState(false);

  const { data, loading, error, refetch } = useQuery<BusinessFunctionsData>(
    GET_QUALITATIVE_FACTORS,
    {
      variables: {
        showDefaults: false,
      },
    },
  );

  const [createQualitativeFactor, { loading: createLoading }] = useMutation(
    CREATE_QUALITATIVE_FACTOR,
    {
      onCompleted: refetch,
    },
  );

  const [updateQualitativeFactor, { loading: updateLoading }] = useMutation(
    UPDATE_QUALITATIVE_FACTOR,
    {
      onCompleted: refetch,
    },
  );

  const [deleteQualitativeFactor, { loading: deleteMutationLoading }] =
    useMutation(DELETE_QUALITATIVE_FACTOR, {
      onCompleted: () => {
        refetch();
        setDeleteModalOpen(false);
        setCurrentRecord(null);
      },
    });

  const qualitativeFactorsData = data?.getQualitativeFactors || [];

  const loadingStates =
    loading || createLoading || updateLoading || deleteMutationLoading;

  const handleDelete = (record: QualitativeFactor) => {
    setCurrentRecord(record);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!currentRecord) return;

    setDeleteLoading(true);
    try {
      await deleteQualitativeFactor({
        variables: {
          uuid: currentRecord.uuid,
        },
      });
    } catch (error) {
      console.error('Failed to delete qualitative factor:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setCurrentRecord(null);
  };

  const extendActions = (record: QualitativeFactor) => (
    <div className="flex items-center gap-2.5">
      <Button
        type="ghost"
        className="hover:bg-transparent !px-2"
        onClick={() => handleDelete(record)}
      >
        <RecycleBin className="hover:text-red-500" />
      </Button>
    </div>
  );

  return (
    <>
      <TitleUpdater title="Qualitative Factors" />
      <TitleBar
        title="Data Inputs"
        subtitle="Qualitative Factors"
        brief="Add Qualitative Factors in the table below, which will allow you to tag applications to them in MEO. Doing so will allow you to create filter reports to show how your various factors depend on IT applications across the organization."
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Qualitative Factors"
          columns={qualitativeFactorsColumns}
          data={qualitativeFactorsData}
          loading={loadingStates}
          editable
          formFields={formFields}
          createGQL={createQualitativeFactor}
          updateGQL={updateQualitativeFactor}
          templateUploadConfig={templateUploadConfig}
          templateColumns={templateColumnConfigs}
          exportExcelColumns={baseColumns}
          inlineEditing
          extendActions={extendActions}
          editableOptions={{
            create: true,
            update: true,
            delete: true,
          }}
          customDeleteHandler={handleDelete}
        />
      </QueryErrorBoundary>

      <QualitativeFactorDeleteModal
        title="Delete Qualitative Factor"
        isOpen={deleteModalOpen}
        onCancel={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
        loading={deleteLoading}
        hasItApplications={currentRecord?.hasItApplications || false}
      />

      <Footer backPath="/data-inputs" />
    </>
  );
};

export default QualitativeFactors;
